/**
 * SurfaceExtrudeArrow — interactive height-axis arrow shared by the three
 * Object Mode surface tools (Surface Box, Surface Cylinder, Surface Poly).
 *
 * Once the silhouette is drawn the camera is unlocked and the user is free to
 * orbit; pulling the silhouette into a 3D primitive then requires grabbing
 * this arrow — the same "must grab the gizmo" pattern used by `OperatorGizmo`
 * for extrude/bevel/inset in Edit Mode.
 *
 * The arrow primitive (`ArrowHandle`) and hit volume (`OP_HIT_HANDLE`) match
 * the operator-gizmo arrow exactly; only the drag math (signed projection
 * along the locked face normal, anchored at the footprint center captured at
 * pointerdown) differs from the existing edit-mode flow.
 *
 * The host gizmo positions / orients / scales the wrapping group via the
 * forwarded ref; this component owns the visual + drag concerns only.
 */

import { type ThreeEvent, useThree } from "@react-three/fiber";
import { forwardRef, useCallback, useEffect, useRef, useState } from "react";
import * as THREE from "three/webgpu";
import { useStore } from "../../app/store.ts";
import { pointerToSignedHeightAlongNormal } from "../../modeling/basisPlaneMath.ts";
import { createRafCoalescer } from "../../rafCoalescer.ts";
import { ArrowHandle } from "./ArrowHandle.tsx";
import { OP_HANDLE_COLOR_HOVER } from "./constants.ts";
import { OP_HIT_HANDLE } from "./gizmoGeometry.ts";

const EMPTY_EXCLUDE = new Set<string>();

const invisibleMaterial = new THREE.MeshBasicMaterial({
  visible: false,
  side: THREE.DoubleSide,
});

export interface SurfaceExtrudeArrowProps {
  /** Visual color for the height handle. Defaults to operator yellow. */
  color?: number;
  /** Hover color for the height handle. Defaults to the operator hover yellow. */
  hoverColor?: number;
  /**
   * Captured at pointerdown: the world-space anchor on the basis plane that
   * the height projection is measured from. Returning `null` aborts the drag.
   */
  computeAnchor: () => readonly [number, number, number] | null;
  /**
   * Captured at pointerdown: the locked face-normal direction (unit vector).
   * Returning `null` aborts the drag.
   */
  computeNormal: () => readonly [number, number, number] | null;
  /** Patch the in-flight draft height per frame. */
  setHeight: (h: number) => void;
  /**
   * Confirm the operator. Sub-epsilon height is rejected inside the store
   * action — it disarms the tool with no scene mutation.
   */
  confirm: () => void;
  /**
   * Notifies the host gizmo when the drag begins / ends so it can lock the
   * camera for the duration. Mirrors `OperatorGizmo`'s `commandBuffer`
   * gating: a gizmo drag should freeze `OrbitControls` so a single gesture
   * can't fight the camera.
   */
  setDragging: (dragging: boolean) => void;
}

/**
 * Wrapping group hosts both the visible arrow and the invisible hit volume so
 * the parent gizmo can drive position / orientation / scale via a single ref
 * — identical to `OperatorGizmo`'s arrow group layout.
 */
export const SurfaceExtrudeArrow = forwardRef<
  THREE.Group,
  SurfaceExtrudeArrowProps
>(function SurfaceExtrudeArrow(
  {
    color,
    hoverColor = OP_HANDLE_COLOR_HOVER,
    computeAnchor,
    computeNormal,
    setHeight,
    confirm,
    setDragging,
  },
  ref,
) {
  const { camera, gl } = useThree();
  const [hovered, setHovered] = useState(false);
  const draggingRef = useRef(false);
  const cleanupRef = useRef<(() => void) | null>(null);

  const onPointerDown = useCallback(
    (e: ThreeEvent<PointerEvent>) => {
      if (draggingRef.current) return;
      // Stop both R3F propagation and the underlying DOM event so the canvas
      // pointerdown listener (regular click-to-select, surface-tool handler)
      // can't fire while the user is grabbing the height handle.
      e.stopPropagation();
      e.nativeEvent.stopImmediatePropagation();

      const anchor = computeAnchor();
      const normal = computeNormal();
      if (!anchor || !normal) return;

      // Snapshot anchor / normal so the projection axis stays locked even if
      // the draft mutates mid-drag (it shouldn't, but defensive).
      const anchorTuple: [number, number, number] = [
        anchor[0],
        anchor[1],
        anchor[2],
      ];
      const normalTuple: [number, number, number] = [
        normal[0],
        normal[1],
        normal[2],
      ];

      draggingRef.current = true;
      // Lock the camera for the duration of this drag — same gating as the
      // edit-mode operator gizmo (where a non-null `commandBuffer` disables
      // `OrbitControls`). Cleared in `cleanup` below so every exit path
      // (release / cancel) restores orbit.
      setDragging(true);
      const canvas = gl.domElement;

      const coalescer = createRafCoalescer<{ x: number; y: number }>(
        ({ x, y }) => {
          const rect = canvas.getBoundingClientRect();
          const ndcX = ((x - rect.left) / rect.width) * 2 - 1;
          const ndcY = -((y - rect.top) / rect.height) * 2 + 1;
          const state = useStore.getState();
          const h = pointerToSignedHeightAlongNormal(
            ndcX,
            ndcY,
            camera,
            rect,
            anchorTuple,
            normalTuple,
            state,
            EMPTY_EXCLUDE,
            x,
            y,
          );
          setHeight(h);
        },
      );

      const cleanup = () => {
        draggingRef.current = false;
        cleanupRef.current = null;
        coalescer.cancel();
        setDragging(false);
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
        window.removeEventListener("pointercancel", onCancel);
      };
      const onMove = (ev: PointerEvent) => {
        coalescer.schedule({ x: ev.clientX, y: ev.clientY });
      };
      const onUp = (ev: PointerEvent) => {
        if (ev.button !== 0) return;
        coalescer.flush();
        cleanup();
        // Confirm AFTER cleanup — the action clears `activeOperator`, which
        // unmounts this component; running listeners would leak otherwise.
        confirm();
      };
      const onCancel = () => {
        cleanup();
      };

      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp);
      window.addEventListener("pointercancel", onCancel);
      cleanupRef.current = cleanup;

      // Drive an immediate frame with the down-event coordinates so a
      // click-without-drag reads as a zero-height confirm path (same as the
      // OperatorGizmo's down-flush behavior).
      coalescer.schedule({ x: e.nativeEvent.clientX, y: e.nativeEvent.clientY });
    },
    [camera, gl, computeAnchor, computeNormal, setHeight, confirm, setDragging],
  );

  const onPointerOver = useCallback((e: ThreeEvent<PointerEvent>) => {
    e.stopPropagation();
    setHovered(true);
  }, []);

  const onPointerOut = useCallback((e: ThreeEvent<PointerEvent>) => {
    e.stopPropagation();
    if (!draggingRef.current) setHovered(false);
  }, []);

  // If the host gizmo unmounts mid-drag (e.g. the operator is disarmed via
  // Escape or external palette toggle), the window listeners installed by
  // `onPointerDown` would otherwise leak. The cleanup also clears the drag
  // flag so a subsequent re-arm starts fresh.
  useEffect(() => {
    return () => {
      cleanupRef.current?.();
    };
  }, []);

  return (
    <group ref={ref}>
      <ArrowHandle color={color} hovered={hovered} hoverColor={hoverColor} />
      <mesh
        geometry={OP_HIT_HANDLE}
        material={invisibleMaterial}
        userData={{ isGizmoHandle: true }}
        onPointerDown={onPointerDown}
        onPointerOver={onPointerOver}
        onPointerOut={onPointerOut}
      />
    </group>
  );
});
